import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CheckoutProductUpgradeExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DynamicRule, RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import {
  LayoutStyle,
  NoMatchingPlanBehavior,
  SubscriptionFrequency,
  UpgradeFormGroup,
  VARIANT_SELECTOR_OPTIONS,
} from '../../models';
import { USE_FIRST_AVAILABLE_PLAN_TOOLTIP } from '../../../../shared/tooltip-texts';

@Component({
  selector: 'product-upgrade-settings',
  templateUrl: './product-upgrade-settings.component.html',
  styleUrls: ['./product-upgrade-settings.component.scss'],
  standalone: false,
})
export class ProductUpgradeSettingsComponent {
  @Input() formGroup: FormGroup<UpgradeFormGroup>;
  @Input() restrictionsRules: DynamicRule[];
  @Input() entities: RuleEntity[];
  @Input() formArrayFieldName: string;

  get allowSubscriptions(): boolean {
    return this.formGroup.controls.settings.controls.allowSubscriptions.value;
  }

  get subscriptionFrequency(): SubscriptionFrequency {
    return this.formGroup.controls.settings.controls.subscriptionFrequency.value;
  }

  get isStrictFrequency(): boolean {
    return this.subscriptionFrequency === 'strict';
  }

  get noMatchingPlanBehavior(): NoMatchingPlanBehavior {
    return this.formGroup.controls.settings.controls.noMatchingPlanBehavior.value;
  }

  get isStandaloneBlock(): boolean {
    return (
      this.formGroup.controls.settings.controls.displayMode.value ===
      CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK
    );
  }

  get layoutStyle(): LayoutStyle {
    return this.formGroup.controls.settings.controls.layoutStyle.value;
  }

  get isFocusedLayout(): boolean {
    return this.isStandaloneBlock && this.layoutStyle === 'focused';
  }

  layoutStyleOptions: { value: LayoutStyle; text: string; description: string }[] = [
    {
      value: 'comparison',
      text: 'Comparison view',
      description:
        'Shows the original cart product alongside its upgrade option, so the shopper can compare both before deciding. For product upgrades only.',
    },
    {
      value: 'focused',
      text: 'Focused Offer',
      description: 'A single dedicated card showing only the upgrade. Required for OTP → Subscription upgrades.',
    },
  ];

  subscriptionFrequencyOptions: { value: SubscriptionFrequency; text: string }[] = [
    { value: 'strict', text: 'Strict' },
    { value: 'flexible', text: 'Flexible' },
  ];

  selectSubscriptionFrequency(value: SubscriptionFrequency): void {
    this.formGroup.controls.settings.controls.subscriptionFrequency.setValue(value);
  }

  selectLayoutStyle(value: LayoutStyle): void {
    this.formGroup.controls.settings.controls.layoutStyle.setValue(value);
    // Focused layout doesn't render Variant Selector or the Revert action; wipe any
    // prior `allowRevert=true` so the persisted state matches the hidden UI.
    if (value === 'focused') {
      this.formGroup.controls.settings.controls.allowRevert.setValue(false);
    } else {
      this.formGroup.controls.appearance.controls.completionBanner.controls.enabled.setValue(false);
    }
  }

  displayModeOptions = [
    {
      text: 'Line Item Action',
      value: CheckoutProductUpgradeExtension.DisplayMode.LINE_ITEM_ACTION,
      description: 'Show upgrade button next to each eligible cart item',
    },
    {
      text: 'Standalone Block',
      value: CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK,
      description: 'Show upgrade options in a separate section',
    },
  ];

  variantSelectorOptions = VARIANT_SELECTOR_OPTIONS;

  readonly useFirstAvailableTooltip = USE_FIRST_AVAILABLE_PLAN_TOOLTIP;

  noMatchingPlanOptions = [
    {
      text: 'Use first available plan',
      value: 'useFirstAvailable',
      description: 'Subscribe customer to the first selling plan offered on the upgrade product.',
    },
    {
      text: 'Hide upgrade',
      value: 'hide',
      description: 'Do not show the upgrade if no compatible subscription plan exists.',
    },
  ];
}
